import {createContext, useState, useEffect, useContext, PropsWithChildren} from 'react';
import AuthData                                                            from 'lib/authData';
import { registerAuthErrorHandler }                                        from 'lib/api/cms';
import { notify }                                                          from './AppNotifications';

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<AuthUser|null>(() => {
    const sessionUser = sessionStorage.getItem('user');
    if (!sessionUser) {
      return null; // User data does not exist
    }

    const userData = JSON.parse(sessionUser) as AuthUser;
    const expires = new Date(userData.tokenExpiry).getTime();
    if (isNaN(expires)) {
      return null; // tokenExpiry is corrupted
    }

    if (expires < new Date().getTime()) {
      return null; // Token is expired
    }

    return userData;
  });

  const handleAuthError = () => {
    notify.info('You have been automatically signed out. Please re-enter your details to continue.', {title: 'Session expired'});
    signOut();
  }

  const signIn = (userData: AuthUser) => {
    setUser(userData);
    // Sign out the standard user
    AuthData.drop();
  }

  const signOut = () => {
    setUser(null);
  }

  useEffect(() => {
    if (user) {
      // We cannot use AuthData here because we must differentiate between standard and anonymous users
      sessionStorage.setItem('user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('user');
    }
  }, [JSON.stringify(user)]);

  // Register the auth error handler to handle 401 errors
  useEffect(() => {
    registerAuthErrorHandler(handleAuthError)
  }, [])

  const contextValue = {
    user,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to consume the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const withAuthContext = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (props: any) => {
    const authProps = useAuth();
    return <Component {...props} authContext={authProps} />;
  }
  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
}

export interface IAuthContext {
  user: AuthUser | null;
  signIn: (userData: AuthUser) => void;
  signOut: () => void;
}

export interface AuthUser {
  userId: number;
  role: string;
  tokenExpiry: number;
  refreshExpiry: number;
}
